import urlConstants from '~/lib/constants/url.constants';
import { HttpRequestMethod } from '~/store';
import { CacheTag } from '~/store/services/CacheTag';
import api from '~/store/services/api';
import { ENUMERATION_CACHE_TIME } from './enumerations';

const {
  addProviderTeamAssignment,
  deleteProviderTeamAssignment,
  getVoidReasons,
} = urlConstants.treatmentEpisodes;
const { updateProviderTeamPreliminaryAssignment } = urlConstants.clinical_data;

interface AddProviderTeamAssignmentParams {
  treatmentEpisodeId: string;
  providerId: string;
  temporary: boolean;
  role: string;
}

interface UpdateProviderTeamAssignmentsParams {
  inquiryId?: string;
  patientId: string;
  treatmentEpisodeId: string;
  providerTeamAssignments: Omit<
    ProviderTeamAssignment,
    'assignmentId' | 'startDate'
  >[];
}
interface UpdateProviderTeamPreliminaryAssignmentsParams {
  inquiryId: string;
  patientId: string;
  providerTeamAssignments: Omit<
    ProviderTeamAssignment,
    'assignmentId' | 'startDate'
  >[];
}

export interface ProviderTeamVoidReason {
  code: string;
  label: string;
}

interface DeleteProviderTeamAssignmentParams {
  treatmentEpisodeId: string;
  assignmentId: string;
  voidReasonCode: string;
  patientId: string;
  inquiryId?: string;
  skipCacheInvalidation?: boolean;
}

export enum EndpointKey {
  ADD_PROVIDER_TEAM_ASSIGNMENT = 'addProviderTeamAssignment',
  UPDATE_PROVIDER_TEAM_ASSIGNMENTS = 'updateProviderTeamAssignments',
  GET_PROVIDER_TEAM_ASSIGNMENT_VOID_REASONS = 'getProviderTeamVoidReasons',
  DELETE_PROVIDER_TEAM_ASSIGNMENT = 'deleteProviderTeamAssignment',
  UPDATE_PROVIDER_TEAM_PRELIMINARY_ASSIGNMENTS = 'updateProviderTeamPreliminaryAssignments',
}

/** Shared cache invalidation tags for mutations that modify provider team assignments. */
const buildAssignmentCacheInvalidationTags = (arg: {
  treatmentEpisodeId: string;
  patientId: string;
  inquiryId?: string;
}) => [
  { id: arg.treatmentEpisodeId, type: CacheTag.TreatmentEpisodes },
  { id: arg.patientId, type: CacheTag.PatientProviderTeamAssignments },
  ...(arg.inquiryId
    ? [{ id: arg.inquiryId, type: CacheTag.Inquiry }]
    : []),
];

export const treatmentEpisodesApi = api.injectEndpoints({
  endpoints: (build) => ({
    [EndpointKey.ADD_PROVIDER_TEAM_ASSIGNMENT]: build.mutation<
      ProviderTeamAssignment,
      AddProviderTeamAssignmentParams
    >({
      invalidatesTags: (result, error, arg) => [
        { id: arg.treatmentEpisodeId, type: CacheTag.TreatmentEpisodes },
      ],

      query: ({ treatmentEpisodeId, providerId, temporary, role }) => ({
        body: {
          providerId,
          role,
          temporary,
        },
        method: HttpRequestMethod.POST,
        url: addProviderTeamAssignment(treatmentEpisodeId),
      }),
    }),

    [EndpointKey.UPDATE_PROVIDER_TEAM_ASSIGNMENTS]: build.mutation<
      ProviderTeamAssignment[],
      UpdateProviderTeamAssignmentsParams
    >({
      invalidatesTags: (result, error, arg) =>
        buildAssignmentCacheInvalidationTags(arg),
      query: ({ treatmentEpisodeId, providerTeamAssignments }) => ({
        body: providerTeamAssignments,
        method: HttpRequestMethod.PUT,
        url: addProviderTeamAssignment(treatmentEpisodeId),
      }),
    }),
    [EndpointKey.UPDATE_PROVIDER_TEAM_PRELIMINARY_ASSIGNMENTS]: build.mutation<
      ProviderTeamAssignment[],
      UpdateProviderTeamPreliminaryAssignmentsParams
    >({
      invalidatesTags: (result, error, arg) => [
        { id: arg.inquiryId, type: CacheTag.TreatmentEpisodes },
        { id: arg.patientId, type: CacheTag.PatientProviderTeamAssignments },
        { id: arg.inquiryId, type: CacheTag.Inquiry },
      ],
      query: ({ inquiryId, providerTeamAssignments }) => ({
        body: providerTeamAssignments,
        method: HttpRequestMethod.PUT,
        url: updateProviderTeamPreliminaryAssignment(inquiryId),
      }),
    }),

    [EndpointKey.GET_PROVIDER_TEAM_ASSIGNMENT_VOID_REASONS]: build.query<
      ProviderTeamVoidReason[],
      void
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      query: () => ({
        url: getVoidReasons,
      }),
    }),

    [EndpointKey.DELETE_PROVIDER_TEAM_ASSIGNMENT]: build.mutation<
      void,
      DeleteProviderTeamAssignmentParams
    >({
      invalidatesTags: (result, error, arg) =>
        arg.skipCacheInvalidation
          ? []
          : buildAssignmentCacheInvalidationTags(arg),
      query: ({ treatmentEpisodeId, assignmentId, voidReasonCode }) => ({
        body: { voidReasonCode },
        method: HttpRequestMethod.DELETE,
        url: deleteProviderTeamAssignment(treatmentEpisodeId, assignmentId),
      }),
    }),
  }),
});

export const {
  useAddProviderTeamAssignmentMutation,
  useUpdateProviderTeamAssignmentsMutation,
  useGetProviderTeamVoidReasonsQuery,
  useDeleteProviderTeamAssignmentMutation,
  useUpdateProviderTeamPreliminaryAssignmentsMutation,
} = treatmentEpisodesApi;
